import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Sparkle, CheckCircle, WarningCircle, ArrowLeft } from "@phosphor-icons/react";
import { RadialBarChart, RadialBar, ResponsiveContainer } from "recharts";

const RUBRIC_KEYS = [
    ["introduction", "Introduction"],
    ["body_content", "Body / Content"],
    ["conclusion", "Conclusion"],
    ["factual_accuracy", "Factual Accuracy"],
    ["language_structure", "Language & Structure"],
];

export default function MainsResult() {
    const { submissionId } = useParams();
    const [s, setS] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/mains/submissions/${submissionId}`);
                setS(data);
            } catch {
                navigate("/mains");
            }
        })();
    }, [submissionId, navigate]);

    if (!s) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

    const e = s.evaluation || {};
    const chartData = e.marks_awarded ? [{ name: "score", value: (e.marks_awarded / 10) * 100, fill: "#F59E0B" }] : null;

    return (
        <Layout>
            <button onClick={() => navigate("/mains")} className="btn-ghost mb-4"><ArrowLeft size={16} /> Back</button>
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-widest text-[#1E3A8A]">{s.subject}</div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl mt-1 leading-snug">{s.prompt}</h1>
            </div>

            {s.ai_error && (
                <div className="mb-6 p-4 rounded-xl border border-red-500/40 bg-red-500/5 text-red-300 text-sm flex items-start gap-2">
                    <WarningCircle size={20} weight="fill" />
                    <div>
                        <div className="font-semibold">AI evaluation couldn't be produced automatically.</div>
                        <div className="mt-1 text-red-200/80">Your submission has been saved for manual review.</div>
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 bento-card">
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Your Answer · {s.word_count} words</div>
                    <div className="whitespace-pre-wrap leading-relaxed text-slate-800 max-h-[600px] overflow-y-auto pr-2">{s.answer_text}</div>
                </div>
                <div className="lg:col-span-5 space-y-5">
                    {chartData && (
                        <div className="bento-card">
                            <div className="flex items-center gap-2 text-[#1E3A8A] text-xs uppercase tracking-widest mb-1"><Sparkle size={14} weight="fill" /> AI Evaluation</div>
                            <div className="flex items-center gap-6 mt-3">
                                <div className="w-28 h-28 shrink-0">
                                    <ResponsiveContainer>
                                        <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" barSize={10} data={chartData} startAngle={90} endAngle={-270}>
                                            <RadialBar background={{ fill: "#1E293B" }} dataKey="value" cornerRadius={10} />
                                        </RadialBarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div>
                                    <div className="font-mono text-4xl font-bold text-[#1E3A8A]">{e.marks_awarded}<span className="text-slate-500 text-xl">/10</span></div>
                                    {e.grade && <div className="text-sm text-slate-600 mt-1">Grade: <span className="font-bold text-[#1E3A8A]">{e.grade}</span></div>}
                                </div>
                            </div>
                        </div>
                    )}

                    {e.rubric && (
                        <div className="bento-card">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Rubric Breakdown</div>
                            <div className="space-y-3">
                                {RUBRIC_KEYS.map(([k, label]) => {
                                    const r = e.rubric[k];
                                    if (!r) return null;
                                    return (
                                        <div key={k}>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-800">{label}</span>
                                                <span className="font-mono text-slate-600">{r.score}/10</span>
                                            </div>
                                            <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                                <div className={`h-full ${r.score >= 7 ? "bg-emerald-500" : r.score >= 4 ? "bg-gold" : "bg-red-500"}`} style={{ width: `${r.score * 10}%` }} />
                                            </div>
                                            {r.feedback && <div className="text-xs text-slate-500 mt-1.5">{r.feedback}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {e.strengths?.length > 0 && (
                        <div className="bento-card">
                            <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Strengths</div>
                            <ul className="text-sm space-y-1.5">
                                {e.strengths.map((x, i) => (
                                    <li key={i} className="flex gap-2 text-slate-800"><CheckCircle size={16} className="text-emerald-400 shrink-0 mt-0.5" weight="fill" /> {x}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {e.improvements?.length > 0 && (
                        <div className="bento-card">
                            <div className="text-xs uppercase tracking-widest text-[#1E3A8A] mb-2">Areas to Improve</div>
                            <ul className="text-sm space-y-1.5">
                                {e.improvements.map((x, i) => (
                                    <li key={i} className="flex gap-2 text-slate-800"><WarningCircle size={16} className="text-[#1E3A8A] shrink-0 mt-0.5" weight="fill" /> {x}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {e.model_answer_outline && (
                        <div className="bento-card">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Model Answer Outline</div>
                            <p className="text-sm text-slate-600 leading-relaxed">{e.model_answer_outline}</p>
                        </div>
                    )}

                    {e.overall_feedback && (
                        <div className="bento-card border-[#1E3A8A]/30">
                            <div className="text-xs uppercase tracking-widest text-[#1E3A8A] mb-2">Overall</div>
                            <p className="text-sm text-slate-800 leading-relaxed">{e.overall_feedback}</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
